import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  RoleListDto,
  DirectoryTableProps,
  Table,
  TextEllipsis,
  TableOperationButton,
  useIsAdmin,
  cleanArray,
} from '@atlas-frontend/sdk';
import { useMemo } from 'react';
import translations from './RoleDirectoryTable.translations.json';
import { createColumnHelper } from '@tanstack/react-table';
import { UiLink } from '../../paths/UiLink';
import { path } from '../../paths';
import { Flex } from 'antd';
import * as Icon from 'react-feather';
import { colorGray } from '@atlas-frontend/design-tokens';
import { useNavigateTo } from '../../paths/useNavigateTo';

const { addTranslations, useTranslation } = i18nNsFactory(
  'role-directory-table'
);
addTranslations(translations);

const columnHelper = createColumnHelper<RoleListDto>();

export type RoleDirectoryTableProps = DirectoryTableProps<any>;

export function RoleDirectoryTable(props: RoleDirectoryTableProps) {
  const { t } = useTranslation();
  const navigateToRoleCreate = useNavigateTo(path.roleCreate);
  const hasPermissionToManage = useIsAdmin();

  const columns = useMemo(
    () =>
      cleanArray([
        columnHelper.accessor('name', {
          header: t('column.name'),
          cell: (props) => (
            <TextEllipsis>
              <UiLink
                id={`role-${props.row.index}`}
                path={path.roleDetails}
                params={{ id: props.row.original.id }}
              >
                {props.renderValue()}
              </UiLink>
            </TextEllipsis>
          ),
          meta: {
            width: '40ch',
          },
        }),
        columnHelper.accessor('permissions', {
          header: t('column.permissions'),
          enableSorting: false,
        }),
        columnHelper.accessor('accessGroups', {
          header: t('column.accessGroups'),
          enableSorting: false,
        }),
        columnHelper.accessor('description', {
          header: t('column.description'),
          meta: {
            minWidth: '30ch',
          },
        }),
        hasPermissionToManage &&
          columnHelper.display({
            id: 'operations',
            header: () => (
              <Flex justify="center" gap="small">
                <TableOperationButton
                  id="create-role"
                  icon={<Icon.Plus size={22} color={colorGray[7]} />}
                  onClick={() => navigateToRoleCreate()}
                  title={t('button.add-role')}
                />
              </Flex>
            ),
            enableSorting: false,
            enableColumnFilter: false,
            meta: {
              width: 50,
              className: 'sticky-right',
            },
          }),
      ]),
    [navigateToRoleCreate, t, hasPermissionToManage]
  );

  return (
    <Table
      name="role-table"
      columns={columns}
      hideClearFilters
      manualFiltering
      manualSorting
      scrollContainerSelector="#role-content"
      sticky
      virtual
      {...props}
    />
  );
}
